/**
 * RetreatBridge — Pure observer of the Engine, acting as a screen router.
 *
 * UI-INV-001: The React UI is a pure observer of the engine.
 * UI-INV-002: RetreatBridge is the sole boundary bridging engine data to React state.
 * UI-INV-004: deriveScreen is the only place screen routing logic exists.
 *
 * M2-UX-P0-006: Removed hardcoded oneDayRetreat — retreat is selected via SelectionScreen.
 * M2-UX-P0-011: Wired scheduling flow (selection → scheduling → home-scheduled).
 * M2-UX-P0-013: Wired library flow (library → retreat-detail → preparation/scheduling).
 */
import { UI_ACTIONS } from "../bridge/uiActionTypes";
import {
  useRef,
  useState,
  useMemo,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { RetreatManager } from "@retreats/engine";
import {
  oneDayRetreat,
  demoRetreat,
  getRetreatBellPatterns,
} from "@retreats/content";
import "../styles/monastery.css";

import { useRetreat } from "../hooks/useRetreat";
import { useMediaSession } from "../hooks/useMediaSession";
import { useRetreatHandlers } from "../hooks/useRetreatHandlers";
import { getTTSEnabled } from "../state/settingsState";
import { getStoredTheme } from "../hooks/getStoredTheme";
import { useUserSchedules } from "../hooks/useUserSchedules";
import type { RetreatSession } from "@retreat/core";
import { ConnectivityBanner } from "./ConnectivityBanner";
import { MissedBellBanner } from "./MissedBellBanner";
import { PushAlarmUnconfirmedBanner } from "./PushAlarmUnconfirmedBanner";
import { TabBar } from "./TabBar";
import { useRetreatStateManagement } from "../hooks/useRetreatStateManagement";
import { useRetreatDeviceIntegration } from "../hooks/useRetreatDeviceIntegration";
import { useRetreatSync } from "../hooks/useRetreatSync";
import { useRetreatEventHandlers } from "../hooks/useRetreatEventHandlers";

// Routing and State
import { createAudioPlayerAdapter } from "../bridge/audioPlayer";
import { renderScreen, type RenderContext, PREFERRED_TRADITION_TO_CONTENT_ID } from "../bridge/renderScreen";
import { audioPlane } from "../planes";

// Hooks
import { useLiveScreenData, type LiveScreenData } from "../hooks/useLiveScreenData";
import { useHomeData } from "../hooks/useHomeData";
import { useCompletionData, type CompletionData } from "../hooks/useCompletionData";
import { useSessionHistory } from "../hooks/useSessionHistory";
import { useAuth } from "../auth/useAuth";
import {
  enqueueReflectionSync,
} from "../services/sessionSyncService";
import { syncManager } from "../services/SyncManager";
import { getActiveSessionId, getAllSessions } from "../state/sessionRepository";
import { getClient } from "@retreats/supabase-adapter";
import { logUIAction, logScreenRender, dumpLogsAsFile } from "../debug/uiLog";
import {
  getDefaultBell,
  setDefaultBell,
  type BellId,
} from "../state/bellState";
import {
  setTTSEnabled,
  setBellsEnabled,
  setWhispersEnabled,
  setPreferredTradition,
  setAudioReflectionEnabled,
  getSettingsSnapshot,
  type PreferredTradition,
} from "../state/settingsState";
import { enqueueSettingsSync } from "../services/sessionSyncService";

import type { UIAction, UIState } from "../bridge/types";
import type { EngineSnapshot } from "@retreats/engine";
import { TabCoordinator } from "../services/TabCoordinator";
import { RetreatState } from "@retreats/engine";
import { STORAGE_KEYS, STORAGE_BUCKETS, PERSISTABLE_SCREENS, type PersistableScreen, VALID_WAKE_OFFSETS, WAKE_OFFSET_MS, type WakeOffset } from "../constants";
import { PushAlarmService } from "../services/PushAlarmService";
import { isDevOrE2E } from "../utils/env";

function _buildScreenDetail(
  screen: string,
  ctx: {
    liveData: LiveScreenData;
    completionData: CompletionData;
    uiState: UIState;
    snapshot: EngineSnapshot;
  },
): Record<string, unknown> {
  switch (screen) {
    case "live":
      return {
        retreatName: ctx.liveData?.retreatName,
        sessionName: ctx.liveData?.session?.name,
        sessionKind: ctx.liveData?.session?.kind,
        remaining: ctx.liveData?.remaining,
        nextSession: ctx.liveData?.next?.name ?? null,
        nextAt: ctx.liveData?.next?.atLabel ?? null,
      };
    case "returning":
      return {
        retreatName: ctx.liveData?.retreatName,
        otherTab: ctx.uiState?.otherTabActive ?? false,
      };
    case "completion":
      return {
        retreatName: ctx.completionData?.retreatName,
        duration: ctx.completionData?.durationLabel,
      };
    case "home":
      return { hasScheduled: ctx.uiState?.hasScheduledRetreat };
    case "end-intention":
      return { intentionKind: ctx.uiState?.intentionRecord?.kind };
    case "history":
    case "settings":
      return {};
    default:
      return {};
  }
}

type Props = { demo?: boolean; testEngine?: boolean };

export function RetreatBridge({ demo = false, testEngine = false }: Props) {
  // 1. Engine state
  const snapshot = useRetreat();

  // ── Audio suspension recovery ────────────────────────────────────────────────
  // Tracks whether the AudioContext has been suspended by iOS/Android after the
  // user backgrounded the app or locked the screen, and AudioContextWatcher's
  // auto-resume has not yet succeeded (no user gesture). When true, LiveScreen
  // renders a banner that calls handleResumeAudio() from a tap (user gesture).
  const [audioSuspended, setAudioSuspended] = useState(false);

  useEffect(() => {
    let attachedCtx: AudioContext | null = null;

    const checkState = () => {
      const ctx = audioPlane.getAudioContext();
      setAudioSuspended(ctx?.state === "suspended");
      if (ctx && ctx !== attachedCtx) {
        attachedCtx?.removeEventListener("statechange", checkState);
        ctx.addEventListener("statechange", checkState);
        attachedCtx = ctx;
      }
    };

    audioPlane.on("status:change", checkState);
    checkState();

    return () => {
      audioPlane.off("status:change", checkState);
      attachedCtx?.removeEventListener("statechange", checkState);
    };
  }, []);

  const handleResumeAudio = useCallback(() => {
    audioPlane.resume().catch(() => {});
  }, []);
  const { signOut, session: authSession, wave, isAdmin } = useAuth();
  const userId = authSession?.userId ?? null;

  // Session history — must be before the login effect that uses refreshHistory
  const { sessions, refresh: refreshHistory } = useSessionHistory();

  // Platform updates — badge count and What's New data; also handles sync + whisper bootstrap
  const { updates, unreadCount: updatesUnreadCount, markAllRead } = useRetreatSync(userId);

  // On login: sync sessions created before auth and refresh history.
  const prevUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (userId && userId !== prevUserIdRef.current) {
      refreshHistory();
    }
    prevUserIdRef.current = userId;
  }, [userId, refreshHistory]);

  // Seed for whisper daily rotation. Using userId keeps the same whisper all day
  // for a given user regardless of page refreshes. Falls back to 'anon' when
  // not logged in so the offline seed pool still rotates deterministically.
  const whisperSeedRef = useRef(userId ?? 'anon');
  useEffect(() => { whisperSeedRef.current = userId ?? 'anon'; }, [userId]);
  const bugLogFileRef = useRef<File | null>(null);

  // 2. UI state
  const { uiState, dispatchUI: _rawDispatchUI, screen } = useRetreatStateManagement(snapshot);
  const personalSchedules = useUserSchedules(uiState.userScheduleVersion);
  const dispatchUI = useCallback(
    (action: UIAction) => {
      logUIAction(action.type);
      if (action.type === "CLOSE_FEEDBACK") bugLogFileRef.current = null;
      if (action.type === "OPEN_FEEDBACK" && wave) {
        // Beta users: auto-capture trace log so it's pre-attached in the feedback form.
        // ??= keeps any log already captured (more specific capture takes precedence).
        try { bugLogFileRef.current ??= dumpLogsAsFile(); } catch { /* non-fatal */ }
      }
      _rawDispatchUI(action);
    },
    [wave],
  );
  // Wire SyncManager so pull() can dispatch SETTINGS_HYDRATED into this reducer
  syncManager.setSyncDispatch(dispatchUI);
  syncManager.configureSettings({
    setBellsEnabled,
    setTTSEnabled,
    setWhispersEnabled,
    setPreferredTradition,
    setAudioReflectionEnabled,
  });

  // 3. Intent handlers (all handlers live in the hook — SRP)
  const audioPlayer = useMemo(() => createAudioPlayerAdapter(audioPlane), []);

  const {
    handlePermissionsGranted,
    handlePermissionsSkip,
    handlePreparationContinue,
    handleReturningDismiss,
    handleReturningTakeover,
    handleReturningEnd,
    handleHearBell,
    handleEndConfirm,
    handleEndCancel,
    handleCompletionReturnHome,
    handleOpenLibrary,
    handleViewRetreatDetail,
    handleCloseLibrary,
    handleSelectRetreat,
    handleConfirmSchedule,
    handleCancelScheduling,
    handleCancelScheduledRetreat,
    handleBeginScheduledRetreat,
    handleScheduleFromDetail,
    handleStartNow,
    handleIntentionComplete,
    handleIntentionDecline,
  } = useRetreatHandlers(
    snapshot,
    audioPlayer,
    dispatchUI,
    RetreatManager.get(),
    uiState.selectedSchedule,
    uiState.preparationSeen,
    uiState.intentionAction,
    getTTSEnabled,
  );

  // ── WakeOffset — persisted to localStorage ─────────────────────────────────
  const [wakeOffset, setWakeOffsetState] = useState<WakeOffset>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.WAKE_OFFSET);
      return VALID_WAKE_OFFSETS.includes(stored as WakeOffset)
        ? (stored as WakeOffset)
        : "30min";
    } catch {
      return "30min";
    }
  });
  const handleChangeWakeOffset = useCallback((v: WakeOffset) => {
    setWakeOffsetState(v);
    try { localStorage.setItem(STORAGE_KEYS.WAKE_OFFSET, v); } catch { /* quota */ }

    // Cancel the old pending alarm and register a new one with the updated offset.
    const rawAt = (() => { try { return localStorage.getItem(STORAGE_KEYS.SCHEDULED_DATETIME); } catch { return null; } })();
    const scheduledAt = rawAt ? Number(rawAt) : null;
    if (!scheduledAt || v === 'self') return;
    const retreatName = uiState.selectedSchedule?.name ?? "";
    PushAlarmService.reschedule(scheduledAt, WAKE_OFFSET_MS[v], retreatName)
      .then((confirmed) => dispatchUI({ type: "PUSH_ALARM_REGISTRATION_RESULT", confirmed }))
      .catch(() => { /* best-effort — alarm re-registers on next confirm */ });
  }, [uiState.selectedSchedule, dispatchUI]);

  // ── Settings / bell / history navigation handlers ─────────────
  const handleOpenSettings = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEYS.LAST_SCREEN, 'settings'); } catch { /* quota */ }
    dispatchUI({ type: UI_ACTIONS.OPEN_SETTINGS });
  }, []);
  const handleCloseSettings = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEYS.LAST_SCREEN, 'home'); } catch { /* quota */ }
    dispatchUI({ type: UI_ACTIONS.CLOSE_SETTINGS });
  }, []);
  const handleCloseHistory = useCallback(() => { dispatchUI({ type: UI_ACTIONS.CLOSE_HISTORY }); }, []);
  const handleOpenBellLibrary = useCallback(() => { dispatchUI({ type: UI_ACTIONS.OPEN_BELL_LIBRARY }); }, []);
  const handleCloseBellLibrary = useCallback(() => { dispatchUI({ type: UI_ACTIONS.CLOSE_BELL_LIBRARY }); }, []);
  const handleCloseBellPreview = useCallback(() => { dispatchUI({ type: UI_ACTIONS.CLOSE_BELL_PREVIEW }); }, []);
  const handleOpenTrust = useCallback(() => { dispatchUI({ type: "NAVIGATE_TO_TRUST" }); }, []);
  const handleOpenTeaching = useCallback(() => { dispatchUI({ type: "OPEN_TEACHING" }); }, []);

  const handlePlaySignal = useCallback(
    (patternId: import("@retreat/core").BellPatternId) => {
      audioPlayer.playPattern(patternId);
    },
    [audioPlayer],
  );

  const handlers = {
    handlePermissionsGranted,
    handlePermissionsSkip,
    handlePreparationContinue,
    handleReturningDismiss,
    handleReturningTakeover,
    handleReturningEnd,
    handleHearBell,
    handleEndConfirm,
    handleEndCancel,
    handleCompletionReturnHome,
    handleOpenLibrary,
    handleViewRetreatDetail,
    handleCloseLibrary,
    handleSelectRetreat,
    handleConfirmSchedule,
    handleCancelScheduling,
    handleCancelScheduledRetreat,
    handleBeginScheduledRetreat,
    handleScheduleFromDetail,
    handleStartNow,
    handleIntentionComplete,
    handleIntentionDecline,
    handleOpenSettings,
    handleCloseSettings,
    handleCloseHistory,
    handleOpenBellLibrary,
    handleCloseBellLibrary,
    handleCloseBellPreview,
    handleOpenTrust,
    handleOpenTeaching,
    handlePlaySignal,
    getRetreatBellPatterns,
  };

  // 3b. In demo mode, capture the test schedule at mount so HoldToBegin can start
  // the engine directly (bypassing Library). Active when __E2E_MODE__ is set (e2e tests)
  // or in DEV mode. Never active in a production deployment without __E2E_MODE__.
  const testScheduleRef = useRef<any>(
    demo && isDevOrE2E()
      ? (window.__retreatTestSchedule ?? null)
      : null,
  );

  // 4. Theme — persisted, defaults to light (parchment)
  const [theme, _setTheme] = useState<"light" | "dark">(getStoredTheme);

  // 4b. Active schedule for live data
  const activeSchedule = useMemo(() => {
    if (demo && testScheduleRef.current) return testScheduleRef.current;
    if (uiState.selectedSchedule) return uiState.selectedSchedule;
    if (demo) return demoRetreat;
    return oneDayRetreat;
  }, [demo, uiState.selectedSchedule]);

  // 4c-5. Device integration: install nudge, SW update, lifecycle boot, wake lock, badges, etc.
  const liveTradId = uiState.preferredTradition != null
    ? PREFERRED_TRADITION_TO_CONTENT_ID[uiState.preferredTradition]
    : undefined;
  const liveData = useLiveScreenData(
    activeSchedule,
    liveTradId,
    uiState.primaryIntentTag ?? undefined,
  );
  const liveInfo = snapshot.status === RetreatState.RUNNING ? {
    sessionName: liveData.session.name,
    sessionKind: liveData.session.kind,
    retreatName: liveData.retreatName,
    elapsedSeconds: liveData.elapsedSeconds,
    totalDurationSeconds: liveData.totalDurationSeconds,
  } : undefined;
  const {
    swUpdatePending,
    applySwUpdate,
    installHandlers: { handleInstallBridge, handleInstallSkip, handleInstallDone, handleInstallBack, isAppInstalled },
  } = useRetreatDeviceIntegration(snapshot, liveInfo, dispatchUI, uiState.isFirstLaunch, demo, testEngine);

  // 5a. After boot completes, restore the last shell screen the user was on.
  // Guards: never restore mid-flow screens; never restore library while RUNNING.
  const wasBootingRef = useRef(true);
  useEffect(() => {
    if (uiState.isBooting || !wasBootingRef.current) return;
    wasBootingRef.current = false;
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.LAST_SCREEN);
      const isValid = stored !== null && PERSISTABLE_SCREENS.includes(stored as PersistableScreen);
      if (!isValid) return;
      const target = stored as PersistableScreen;
      if ((target === 'library' || target === 'retreat-detail') && snapshot.status === RetreatState.RUNNING) return;
      if (target === 'library' || target === 'retreat-detail') dispatchUI({ type: 'OPEN_LIBRARY' });
      else if (target === 'history') dispatchUI({ type: 'OPEN_HISTORY' });
      else if (target === 'settings') dispatchUI({ type: 'OPEN_SETTINGS' });
      // 'home' is the default — no action needed
    } catch {
      // localStorage unavailable
    }
  }, [uiState.isBooting, snapshot.status, dispatchUI]);

  // 5b. Event handlers: retreat events, click capture, missed bell, armed window
  const { missedBell } = useRetreatEventHandlers(
    snapshot,
    uiState,
    liveData,
    dispatchUI,
    handlePreparationContinue,
    activeSchedule,
    userId,
  );

  useMediaSession(liveInfo);
  const homeData = useHomeData(uiState.selectedSchedule);
  // Use stored elapsed from UIState — captured before engine.stop() reset it to 0
  const completionData = useCompletionData(
    activeSchedule,
    uiState.completionElapsedSeconds || snapshot.elapsedSeconds,
    uiState.showCompletion,
  );
  const [selectedHistorySession, setSelectedHistorySession] = useState<RetreatSession | null>(null);

  // Bell + settings state (re-read on demand — no need for React state since
  // these screens are only shown when explicitly opened)
  const [defaultBellId, setDefaultBellState] = useState<BellId>(() =>
    getDefaultBell(),
  );
  const handleSetDefaultBell = useCallback((id: BellId) => {
    setDefaultBell(id);
    setDefaultBellState(id);
  }, []);

  const handleToggleTTS = useCallback((value: boolean) => {
    setTTSEnabled(value);
    RetreatManager.get().getSystemAlertManager().setEnabled(value);
    if (userId) { enqueueSettingsSync(userId, getSettingsSnapshot()); void syncManager.drain(); }
    dispatchUI({ type: 'SETTINGS_HYDRATED' });
  }, [userId]);

  const handleToggleBells = useCallback((value: boolean) => {
    setBellsEnabled(value);
    if (userId) { enqueueSettingsSync(userId, getSettingsSnapshot()); void syncManager.drain(); }
    dispatchUI({ type: 'SETTINGS_HYDRATED' });
  }, [userId]);

  const handleToggleWhispers = useCallback((value: boolean) => {
    setWhispersEnabled(value);
    if (userId) { enqueueSettingsSync(userId, getSettingsSnapshot()); void syncManager.drain(); }
    dispatchUI({ type: 'SETTINGS_HYDRATED' });
  }, [userId]);

  const handleSetTradition = useCallback((value: PreferredTradition) => {
    setPreferredTradition(value);
    if (userId) { enqueueSettingsSync(userId, getSettingsSnapshot()); void syncManager.drain(); }
    dispatchUI({ type: 'SETTINGS_HYDRATED' });
  }, [userId]);

  const handleToggleAudioReflection = useCallback((value: boolean) => {
    setAudioReflectionEnabled(value);
    if (userId) { enqueueSettingsSync(userId, getSettingsSnapshot()); void syncManager.drain(); }
    dispatchUI({ type: 'SETTINGS_HYDRATED' });
  }, [userId]);


  const handlePreviewBell = useCallback(
    (id: BellId) => {
      dispatchUI({ type: "PREVIEW_BELL", bellId: id });
      audioPlayer.playInstrumentPreview(id);
    },
    [audioPlayer],
  );

  // ── Reflection handlers ────────────────────────────────────────
  const handleCompletionWithReflection = useCallback(() => {
    dispatchUI({ type: "SHOW_REFLECTION" });
  }, []);

  const handleSaveReflection = useCallback(
    async (text: string, photo: File | null) => {
      let photoUrls: string[] = [];

      if (photo && userId) {
        try {
          const ext = photo.name.split(".").pop() ?? "jpg";
          const path = `${userId}/${Date.now()}.${ext}`;
          const { error: uploadError } = await getClient()
            .storage.from(STORAGE_BUCKETS.REFLECTION_PHOTOS)
            .upload(path, photo, { upsert: false });
          if (!uploadError) {
            const { data: urlData } = getClient()
              .storage.from(STORAGE_BUCKETS.REFLECTION_PHOTOS)
              .getPublicUrl(path);
            if (urlData.publicUrl) photoUrls = [urlData.publicUrl];
          }
        } catch {
          // Photo upload failure is non-fatal — diary text still saves
        }
      }

      if (completionData.completedAt) {
        // getActiveSessionId() returns null after endSession() clears it during the
        // RUNNING→IDLE transition. Fall back to getAllSessions()[0].id — the session
        // that was just ended is always the most recently started one.
        const sessionId = getActiveSessionId() ?? getAllSessions()[0]?.id ?? null;
        if (sessionId && userId) {
          enqueueReflectionSync(sessionId, text, completionData.completedAt, userId, null, photoUrls);
          void syncManager.drain();
        }
      }
      // Dismiss both reflection and the underlying completion ceremony
      dispatchUI({ type: "DISMISS_REFLECTION" });
      dispatchUI({ type: "DISMISS_COMPLETION" });
      handleCompletionReturnHome();
    },
    [completionData.completedAt, handleCompletionReturnHome, userId],
  );

  const handleSkipReflection = useCallback(() => {
    dispatchUI({ type: "DISMISS_REFLECTION" });
    dispatchUI({ type: "DISMISS_COMPLETION" });
    handleCompletionReturnHome();
  }, [handleCompletionReturnHome]);

  // Log screen renders after React commits — only when screen actually changes.
  // All deps are listed so the closure is fresh, but the prevRef guard ensures
  // we only fire once per screen transition (not on every liveData timer tick).
  const _prevScreenRef = useRef<string>("");
  useEffect(() => {
    if (screen === _prevScreenRef.current) return;
    _prevScreenRef.current = screen;
    logScreenRender(screen, _buildScreenDetail(screen, {
      liveData,
      completionData,
      uiState,
      snapshot,
    }));
  }, [screen, liveData, completionData, uiState, snapshot]);

  // End gesture: intercept with Buddha moment if intention is set
  const handleEndGesture = useCallback(() => {
    const r = uiState.intentionRecord;
    if (r.kind === "set") {
      dispatchUI({ type: "SHOW_END_INTENTION" });
    } else {
      dispatchUI({ type: "SHOW_END_CONFIRM" });
    }
  }, [uiState.intentionRecord, dispatchUI]);

  // Guard: set true before stop() when this tab loses ownership, to suppress
  // broadcastEnd (we didn't end the retreat — another tab took over).
  const isOwnershipTransferRef = useRef(false);
  const wasRunningRef = useRef(false);
  // Guard: prevent concurrent handleContinueRetreat calls (double-tap on resume card).
  const isContinuingRef = useRef(false);

  // Register TabCoordinator callbacks once on mount.
  useEffect(() => {
    TabCoordinator.get().onOwnershipTransfer((retreatName) => {
      // Only stop if this tab was actually running — idle observer tabs receive
      // the broadcast too but have nothing to stop.
      const mgr = RetreatManager.get();
      if (mgr.getState() === RetreatState.RUNNING) {
        // Set ref BEFORE stop() so the status-sync effect skips broadcastEnd.
        isOwnershipTransferRef.current = true;
        // keepPersistence: true — leave IDB intact so the new controller can probe it.
        // stop() normally clears IDB, which would race with the new controller's probe().
        void mgr.stop({ keepPersistence: true });
      }
      // Keep the resume card visible so the user can reclaim the retreat.
      dispatchUI({ type: "OTHER_TAB_ACTIVE", retreatName: retreatName ?? undefined });
    });

    TabCoordinator.get().onRetreatEnded(() => {
      dispatchUI({ type: "RETREAT_ENDED_GLOBALLY" });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep TabCoordinator in sync with the engine's running state.
  // Broadcasts retreat-end to other tabs on genuine RUNNING → IDLE (not ownership transfer).
  useEffect(() => {
    if (snapshot.status === RetreatState.RUNNING) {
      wasRunningRef.current = true;
      TabCoordinator.get().setActive(activeSchedule.id ?? "unknown", activeSchedule.name ?? null);
    } else if (snapshot.status === RetreatState.IDLE && wasRunningRef.current) {
      wasRunningRef.current = false;
      if (!isOwnershipTransferRef.current) {
        TabCoordinator.get().broadcastEnd();
      }
      TabCoordinator.get().setInactive();
      isOwnershipTransferRef.current = false;
    } else {
      TabCoordinator.get().setInactive();
    }
  }, [snapshot.status, activeSchedule.id, activeSchedule.name]);

  // Unified resume handler for the home-screen resume card.
  // Same-tab refresh: engine is PROBED — confirmResume starts it.
  // Other-tab active: probe IDB first (before the old controller clears it),
  //   then transfer ownership, then confirmResume from the captured session.
  const handleContinueRetreat = useCallback(async () => {
    if (isContinuingRef.current) return;
    isContinuingRef.current = true;
    try {
      if (uiState.otherTabActive) {
        const mgr = RetreatManager.get();
        // Re-select the retreat to restore session context — _shutdownManagers() during the
        // previous stop() cleared SessionManager.activeSession, so probe() and initialize()
        // would both fail without it. (INV-025)
        mgr.selectRetreat(activeSchedule);
        await mgr.probe(activeSchedule.id, activeSchedule.version);
        const pending = mgr.getSnapshot().pendingSession;

        TabCoordinator.get().transferOwnership(
          activeSchedule.id ?? "unknown",
          uiState.otherTabRetreatName,
        );
        dispatchUI({ type: "TAKE_OWNERSHIP" });

        if (pending) {
          await mgr.confirmResume(pending);
        }
      } else {
        // Same-tab path: pendingSession is already set from probe() during boot.
        await handleReturningTakeover();
      }
    } finally {
      isContinuingRef.current = false;
    }
  }, [uiState.otherTabActive, uiState.otherTabRetreatName, activeSchedule, handleReturningTakeover, dispatchUI]);

  // 8. Content
  const ctx: RenderContext = {
    uiState,
    snapshot,
    handlers,
    installHandlers: {
      handleInstallBridge,
      handleInstallSkip,
      handleInstallDone,
      handleInstallBack,
      isAppInstalled,
      swUpdatePending,
      applySwUpdate,
    },
    liveData,
    homeData,
    completionData,
    sessions,
    defaultBellId,
    ttsEnabled: uiState.ttsEnabled,
    activeSchedule,
    demo,
    signOut,
    dispatchUI,
    handleSetDefaultBell,
    handleToggleTTS,
    handleEndGesture,
    handlePreviewBell,
    handleSaveReflection,
    handleSkipReflection,
    handleCompletionWithReflection,
    handleContinueRetreat,
    demoRetreat,
    selectedHistorySession,
    onSelectSession: setSelectedHistorySession,
    onCloseSessionDetail: () => setSelectedHistorySession(null),
    // Settings v2 — sourced from uiState so SETTINGS_HYDRATED refreshes them atomically
    bellsEnabled: uiState.bellsEnabled,
    whispersEnabled: uiState.whispersEnabled,
    preferredTradition: uiState.preferredTradition,
    audioReflectionEnabled: uiState.audioReflectionEnabled,
    handleToggleBells,
    handleToggleWhispers,
    handleSetTradition,
    handleToggleAudioReflection,
    intentLabels: uiState.intentLabels,
    primaryIntentTag: uiState.primaryIntentTag,
    wakeOffset,
    handleChangeWakeOffset,
    lastBellFiredAt: snapshot.lastBellFiredAt ?? null,
    wave,
    isAdmin,
    userId: authSession?.userId ?? "",
    userEmail: authSession?.email ?? "",
    whisperSeed: whisperSeedRef.current,
    bugLogFile: bugLogFileRef.current,
    personalSchedules,
    audioSuspended,
    handleResumeAudio,
    updates,
    updatesUnreadCount,
    onMarkAllRead: markAllRead,
  };

  // Only show TabBar on primary navigation screens.
  // Settings and bell sub-screens are reached via the gear icon — no tab maps to them.
  // Active home (returning or other-tab) intentionally hides the tab bar.
  const SHELL_SCREENS = new Set([
    "home", "home-scheduled", "selection", "library", "retreat-detail",
    "scheduling", "history",
  ]);
  const isActiveHome = screen === "home" && (uiState.isReturning || uiState.otherTabActive);
  const showTabBar = SHELL_SCREENS.has(screen) && !isActiveHome;

  let content: ReactNode = renderScreen(screen, ctx);

  return (
    <div className="rc-app" data-theme={theme}>
      <ConnectivityBanner />
      <MissedBellBanner visible={missedBell.visible} dismiss={missedBell.dismiss} />
      <PushAlarmUnconfirmedBanner visible={uiState.pushAlarmUnconfirmed && uiState.hasScheduledRetreat} />
      {content}
      {showTabBar && (
        <TabBar
          activeTab={
            screen === "history"
              ? "history"
              : screen === "library" || screen === "retreat-detail"
                ? "discover"
                : "home"
          }
          onHome={() => {
            try { localStorage.setItem(STORAGE_KEYS.LAST_SCREEN, 'home'); } catch { /* quota */ }
            dispatchUI({ type: UI_ACTIONS.CLOSE_HISTORY });
            dispatchUI({ type: UI_ACTIONS.CLOSE_SETTINGS });
            dispatchUI({ type: UI_ACTIONS.CLOSE_LIBRARY });
          }}
          onDiscover={() => {
            try { localStorage.setItem(STORAGE_KEYS.LAST_SCREEN, 'library'); } catch { /* quota */ }
            handleOpenLibrary();
          }}
          onHistory={() => {
            try { localStorage.setItem(STORAGE_KEYS.LAST_SCREEN, 'history'); } catch { /* quota */ }
            refreshHistory();
            setSelectedHistorySession(null);
            dispatchUI({ type: UI_ACTIONS.OPEN_HISTORY });
          }}
        />
      )}
    </div>
  );
}
